import { Box, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import { Grid } from "@mui/material";
import { EventType } from "./DiaryEntryIssuesView";
import { useContext, useState } from "react";
import { StyledRemoveIssue } from "../StyledComponents";
import CloseIcon from '@mui/icons-material/Close';
import { useMutation } from "@apollo/client/react";
import { titleize } from "inflection";
import PermissionContext from "@/Contexts/Permissions";
import { ReportContext } from "@/Contexts/ReportContext";
import { PermissionActionEnum, PermissionEntityEnum, DropEventDocument, Event } from "@/graphql/generated/graphql";
import { displayNotification } from "@/utils/notifications";
import EditEventModal from "@/components/Events/EditEventModal";
import { toDecimalHours } from "@/utils/dateTimeHelpers";
import CurrentUserContext from "@/Contexts/CurrentUser";

interface DiaryEntryEventsRendererProps {
  positionedEvents: Array<EventType>;
  issuesCount: number;
  cellSize: number;
}

const DiaryEntryEventsRenderer: React.FC<DiaryEntryEventsRendererProps> = ({
  positionedEvents,
  issuesCount,
  cellSize,
}) => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const { can } = useContext(PermissionContext);
  const { Manage } = PermissionActionEnum;
  const { Events } = PermissionEntityEnum;

  const { selectedProject, selectedReport } = useContext(ReportContext);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const [dropEvent] = useMutation(DropEventDocument, {
    onCompleted: () => {
      displayNotification({ type: "success", text: "Event dropped successfully." })
    },
    onError: (err) => {
      displayNotification({ type: "error", text: `Event could not be dropped: ${err.message}` })
    }
  });

  const handleRemoveEvent = (id: string) => {
    if (!can(Manage, Events, { projectId: selectedProject?.id })) return;
    dropEvent({
      variables: { id },
      update(cache) {
        cache.evict({ id: cache.identify({ __typename: 'Event', id }) });
        cache.gc();
      }
    })
  };

  const getEventToolTipTitle = (event: EventType) => {
    return (
      <Grid container direction="column" spacing={1} width={300}>
        <Typography variant="subtitle1" fontWeight="bold">
          {event.title}
        </Typography>
        <Typography variant="body2">
          <strong>Occurred At:</strong> {dayjs(event.occurredAt).format(dateTimePreference.time)}
        </Typography>
        <Typography variant="body2">
          <strong>Type:</strong> {event.eventType}
        </Typography>
        {event.description && (
          <Typography variant="body2">
            <strong>Description:</strong> {event.description}
          </Typography>
        )}
        {event.status && (
          <Typography variant="body2">
            <strong>Status:</strong> {titleize(event.status)}
          </Typography>
        )}
      </Grid>
    );
  };

  return (
    <>
      {positionedEvents.map((event, index) => {
        const eventHour = toDecimalHours(dayjs(event.occurredAt));
        const eventWidth = 60;

        return (
          <Tooltip
            key={`event-card-${event.id}`}
            title={getEventToolTipTitle(event)}
            placement="bottom"
            arrow
          >
            <Box
              id={`diary-entry-event-${event.id}`}
              sx={{
                position: 'absolute',
                left: `${eventHour * cellSize}px`,
                top: `${(issuesCount + index) * 50}px`,
                width: `${eventWidth}px`,
                height: '40px',
                backgroundColor: 'rgba(33, 150, 243, 0.8)',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(33, 150, 243, 1)',
                  transform: 'scale(1.2)',
                  zIndex: 10
                },
                '&:hover .close-icon': {
                  opacity: 1,
                }
              }}
              onClick={() => setSelectedEvent(event)}
            >
              <StyledRemoveIssue
                className="close-icon"
                sx={{ right: -8 }}
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemoveEvent(event.id);
                }}
              >
                <CloseIcon fontSize="small" />
              </StyledRemoveIssue>
              <Typography variant="body1" noWrap pl={1}>
                {event.title}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}

      {
        selectedEvent &&
        <EditEventModal
          event={selectedEvent}
          project={selectedProject}
          reportDate={selectedReport?.reportDate}
          handleClose={() => {
            setSelectedEvent(null);
          }}
        />
      }
    </>
  );
};

export default DiaryEntryEventsRenderer;
